import { Button, Radio, Spinner, Tooltip } from '@dixa/lib-shared-habla';
import { boundMethod } from 'autobind-decorator';
import { AsyncStatus } from 'lib-shared-legacy-types';
import * as React from 'react';

import {
  findLabelByLanguageCode,
  LanguageCode,
} from 'app/utils/supportedLanguages';

import styles from './satisfactionDropdown.module.scss';

export type SatisfactionDropdownProps = {
  /** The selected language code. */
  selected: LanguageCode;
  /** The selectable languages. */
  languageCodes: LanguageCode[];
  /** Whether or not it can be cancelled. */
  isCancellable: boolean;
  /** The async status of the languages. */
  languageCodesAsyncStatus: AsyncStatus;
  /** The async status of the cancel operation. */
  cancelAsyncStatus: AsyncStatus;
  /** Invoked whenever the language is changed. */
  onChange: (language: LanguageCode) => void;
  /** Invoked whenever it is cancelled. */
  onCancel: () => void;
  /** Invoked whenever the dropdown is toggled. */
  toggleDropdown: () => void;
};

const BUTTON_TEXT_BY_ASYNC_STATUS = {
  loading: 'Cancelling...',
  succeeded: 'Cancel CSAT',
  failed: 'Error',
};

export class SatisfactionDropdown extends React.Component<SatisfactionDropdownProps> {
  @boundMethod
  handleOnCancelClick() {
    const { toggleDropdown, onCancel } = this.props;
    onCancel();
    toggleDropdown();
  }

  @boundMethod
  handleLanguageSelection(languageCode: LanguageCode) {
    const { toggleDropdown, onChange } = this.props;
    onChange(languageCode);
    toggleDropdown();
  }

  @boundMethod
  renderLanguage(languageCode: LanguageCode) {
    const { selected } = this.props;
    const id = `satisfaction-dropdown-${languageCode}`;
    const label = findLabelByLanguageCode(languageCode);

    return (
      <div className={styles.language} key={id}>
        <div>
          <Radio
            className={styles.languageRadio}
            id={id}
            name="satisfaction-language"
            checked={languageCode === selected}
            onChange={() => this.handleLanguageSelection(languageCode)}
          />
        </div>

        <Tooltip content={label} dark>
          <label htmlFor={id} className={styles.languageCode}>
            {languageCode}
          </label>
        </Tooltip>
      </div>
    );
  }

  renderLanguages(): React.ReactNode {
    const { languageCodes, languageCodesAsyncStatus } = this.props;

    if (languageCodesAsyncStatus === 'loading') {
      return (
        <div className={styles.loading}>
          <Spinner size={16} />
        </div>
      );
    }
    if (languageCodesAsyncStatus === 'succeeded') {
      return languageCodes.map(this.renderLanguage);
    }

    return null;
  }

  renderCancelButton() {
    const { cancelAsyncStatus } = this.props;
    const isLoading = cancelAsyncStatus === 'loading';
    const disabled = cancelAsyncStatus !== 'succeeded';
    const text = BUTTON_TEXT_BY_ASYNC_STATUS[cancelAsyncStatus];

    const variant = cancelAsyncStatus === 'failed' ? 'danger' : 'secondary';

    return (
      <Button
        isLoading={isLoading}
        disabled={disabled}
        variant={variant}
        onClick={this.handleOnCancelClick}
      >
        {text}
      </Button>
    );
  }

  render() {
    const { isCancellable } = this.props;

    return (
      <div className={styles.root}>
        <div className={styles.languagesWrapper}>
          <div className={styles.languages}>{this.renderLanguages()}</div>
        </div>
        {isCancellable && <div>{this.renderCancelButton()}</div>}
      </div>
    );
  }
}
